<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import nexceoProjectsAPI from 'dashboard/api/nexceo/projects';

const router = useRouter();
const { accountScopedRoute } = useAccount();
// M29.5.16A follow-up — this page is now reachable by any project member
// (backend GET /nexceo/projects scopes the list to the current user's own
// projects — see Nexceo::AccessPolicy#scope_projects), but creating a
// project, editing project details, and AI employee config all stay
// administrator-only routes/actions. Row click and the per-row action
// buttons are adjusted below so a non-admin never lands on a route that
// will just bounce them back to the dashboard.
const { isAdmin } = useAdmin();

const projects = ref([]);
const isLoading = ref(false);
const error = ref(null);

const STATUS_BADGE = {
  under_construction:
    'bg-amber-50 text-amber-800 dark:bg-amber-800/20 dark:text-amber-400',
  ready_to_move:
    'bg-success-50 text-success-800 dark:bg-success-800/20 dark:text-success-400',
  sold: 'bg-n-alpha-2 text-n-slate-8',
};
const STATUS_EMOJI = {
  under_construction: '🟡',
  ready_to_move: '🟢',
  sold: '⚫',
};
const STATUS_LABELS = {
  under_construction: 'Under Construction',
  ready_to_move: 'Ready to Move',
  sold: 'Sold',
};

function statusSubtext(project) {
  if (project.status === 'under_construction') {
    return project.delivery_year ? `Delivery ${project.delivery_year}` : '';
  }
  if (project.status === 'ready_to_move') return 'Immediate Possession';
  return '';
}

async function loadProjects() {
  isLoading.value = true;
  error.value = null;
  try {
    const { data } = await nexceoProjectsAPI.get();
    projects.value = Array.isArray(data) ? data : (data?.projects ?? []);
  } catch {
    error.value = 'Failed to load projects. Please try again.';
  } finally {
    isLoading.value = false;
  }
}

function editProject(project) {
  router.push(accountScopedRoute('nexceo_projects_edit', { id: project.id }));
}

function newProject() {
  router.push(accountScopedRoute('nexceo_projects_new'));
}

function openAiSettings(project) {
  router.push(
    accountScopedRoute('nexceo_ai_employee_settings', { id: project.id })
  );
}

function openDocuments(project) {
  router.push(
    accountScopedRoute('nexceo_project_documents', { id: project.id })
  );
}

function openInventory(project) {
  router.push(
    accountScopedRoute('nexceo_project_inventory', { id: project.id })
  );
}

// Non-admins can't reach nexceo_projects_edit (admin-only route), so their
// row click opens Documents instead — the closest thing to "open this
// project" a project member (team leader/agent) can actually get into.
function openProject(project) {
  if (isAdmin.value) {
    editProject(project);
    return;
  }
  openDocuments(project);
}

function formatPriceRange(project) {
  if (!project.price_min && !project.price_max) return '—';
  const sym = project.currency === 'INR' ? '₹' : project.currency || '';
  const fmt = v => {
    if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
    if (v >= 100000) return `${(v / 100000).toFixed(0)}L`;
    return Number(v).toLocaleString();
  };
  if (project.price_min && project.price_max) {
    return `${sym}${fmt(project.price_min)}–${sym}${fmt(project.price_max)}`;
  }
  return `${sym}${fmt(project.price_min || project.price_max)}`;
}

onMounted(loadProjects);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <!-- Header -->
    <div
      class="flex items-center justify-between px-4 h-14 border-b border-n-weak bg-n-background shrink-0"
    >
      <div class="flex items-center gap-2">
        <span class="i-lucide-folder-open size-4 text-woot-500" />
        <h1 class="text-base font-semibold text-n-slate-12">Projects</h1>
        <span
          v-if="projects.length"
          class="text-xs font-medium px-2 py-0.5 rounded-full bg-n-alpha-2 text-n-slate-9"
        >
          {{ projects.length }}
        </span>
      </div>
      <button
        v-if="isAdmin"
        class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-woot-500 text-white text-sm font-medium hover:bg-woot-600 transition-colors"
        @click="newProject"
      >
        <span class="i-lucide-plus size-4" />
        New Project
      </button>
    </div>

    <!-- Loading -->
    <div
      v-if="isLoading"
      class="flex flex-col items-center justify-center gap-2 flex-1"
    >
      <span class="i-lucide-loader-2 size-6 animate-spin text-woot-500" />
      <span class="text-sm text-n-slate-9">Loading projects</span>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="flex items-center justify-center flex-1">
      <div class="text-center">
        <span
          class="i-lucide-alert-circle size-8 text-ruby-500 mx-auto block mb-2"
        />
        <p class="text-sm text-n-slate-11">{{ error }}</p>
        <button
          class="mt-3 text-sm text-woot-500 hover:underline"
          @click="loadProjects"
        >
          Try again
        </button>
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="!projects.length"
      class="flex items-center justify-center flex-1"
    >
      <div class="text-center max-w-sm">
        <div
          class="mx-auto mb-4 flex items-center justify-center w-14 h-14 rounded-2xl bg-woot-50 dark:bg-woot-500/10"
        >
          <span class="i-lucide-building-2 size-7 text-woot-500" />
        </div>
        <h3 class="text-base font-semibold text-n-slate-12 mb-1">
          No projects yet
        </h3>
        <p class="text-sm text-n-slate-9 mb-5">
          {{
            isAdmin
              ? 'Create your first real-estate project. An AI employee and Kanban stages will be set up automatically.'
              : "You haven't been assigned to a project yet. Ask an admin to add you as a team member."
          }}
        </p>
        <button
          v-if="isAdmin"
          class="px-4 py-2 rounded-lg bg-woot-500 text-white text-sm font-medium hover:bg-woot-600 transition-colors"
          @click="newProject"
        >
          Create Project
        </button>
      </div>
    </div>

    <!-- Project table -->
    <div v-else class="flex-1 overflow-auto">
      <table class="w-full min-w-[560px] text-sm">
        <thead class="sticky top-0 z-10 bg-n-background border-b border-n-weak">
          <tr>
            <th
              class="text-left px-4 py-2.5 text-xs font-semibold text-n-slate-9 uppercase tracking-wide"
            >
              Project
            </th>
            <th
              class="text-left px-4 py-2.5 text-xs font-semibold text-n-slate-9 uppercase tracking-wide hidden md:table-cell"
            >
              Location
            </th>
            <th
              class="text-left px-4 py-2.5 text-xs font-semibold text-n-slate-9 uppercase tracking-wide hidden lg:table-cell"
            >
              Price Range
            </th>
            <th
              class="text-left px-4 py-2.5 text-xs font-semibold text-n-slate-9 uppercase tracking-wide"
            >
              Status
            </th>
            <th
              class="text-left px-4 py-2.5 text-xs font-semibold text-n-slate-9 uppercase tracking-wide"
            >
              AI
            </th>
            <th class="w-24" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="project in projects"
            :key="project.id"
            class="border-b border-n-weak hover:bg-n-alpha-1 transition-colors cursor-pointer group"
            @click="openProject(project)"
          >
            <td class="px-4 py-3">
              <div class="flex items-center gap-3">
                <div
                  class="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg bg-woot-50 dark:bg-woot-500/10"
                >
                  <span class="i-lucide-building-2 size-3.5 text-woot-500" />
                </div>
                <div>
                  <p class="font-medium text-n-slate-12">{{ project.name }}</p>
                  <p
                    v-if="project.property_type"
                    class="text-xs text-n-slate-9 mt-0.5"
                  >
                    {{ project.property_type }}
                  </p>
                </div>
              </div>
            </td>
            <td class="px-4 py-3 text-n-slate-11 hidden md:table-cell">
              {{
                [project.city, project.state].filter(Boolean).join(', ') ||
                project.location ||
                '—'
              }}
            </td>
            <td class="px-4 py-3 text-n-slate-11 hidden lg:table-cell">
              {{ formatPriceRange(project) }}
            </td>
            <td class="px-4 py-3">
              <span
                class="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full"
                :class="
                  STATUS_BADGE[project.status] ?? 'bg-n-alpha-2 text-n-slate-8'
                "
              >
                {{ STATUS_EMOJI[project.status] }}
                {{ STATUS_LABELS[project.status] ?? project.status }}
              </span>
              <p
                v-if="statusSubtext(project)"
                class="text-xs text-n-slate-8 mt-1"
              >
                {{ statusSubtext(project) }}
              </p>
            </td>
            <td class="px-4 py-3">
              <span
                v-if="project.ai_employee"
                class="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full"
                :class="
                  project.ai_employee.status === 'active'
                    ? 'bg-success-50 text-success-800 dark:bg-success-800/20 dark:text-success-400'
                    : 'bg-n-alpha-2 text-n-slate-8'
                "
              >
                <span class="i-lucide-bot size-3" />
                {{ project.ai_employee.status }}
              </span>
              <span v-else class="text-xs text-n-slate-7">—</span>
            </td>
            <td class="px-4 py-3 text-right" @click.stop>
              <div
                class="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <button
                  v-if="isAdmin"
                  class="flex items-center justify-center w-8 h-8 rounded-lg text-n-slate-8 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
                  title="AI Employee Settings"
                  @click="openAiSettings(project)"
                >
                  <span class="i-lucide-bot size-4" />
                </button>
                <button
                  class="flex items-center justify-center w-8 h-8 rounded-lg text-n-slate-8 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
                  title="Documents"
                  @click="openDocuments(project)"
                >
                  <span class="i-lucide-file-text size-4" />
                </button>
                <button
                  class="flex items-center justify-center w-8 h-8 rounded-lg text-n-slate-8 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
                  title="Inventory"
                  @click="openInventory(project)"
                >
                  <span class="i-lucide-layout-grid size-4" />
                </button>
                <button
                  v-if="isAdmin"
                  class="flex items-center justify-center w-8 h-8 rounded-lg text-n-slate-8 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
                  title="Edit Project"
                  @click="editProject(project)"
                >
                  <span class="i-lucide-pencil size-4" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
